import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import en from "react-phone-number-input/locale/en.json";
import {
  X,
  HeartHandshake,
  Loader2,
  CheckCircle2,
  Download,
  ChevronDown,
  Check,
  Search,
  Video,
  Handshake,
  Newspaper,
  Users,
  MessageSquare,
  Plane,
  Shield,
  Zap,
} from "lucide-react";
import { db } from "../../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import {
  prefetchDownload,
  triggerPrefetchedDownload,
} from "../../utils/downloadMap";

const ContactModal = ({
  isOpen,
  onClose,
  intent = "contact",
  downloadKey = "",
}) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [phone, setPhone] = useState("");
  const [industry, setIndustry] = useState("");
  const [interest, setInterest] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});

  const handleBackToHome = () => {
    onClose();
    navigate("/");
  };

  const isDownload = intent === "download" && downloadKey;
  const isWalkthrough = intent === "download" && !downloadKey;
  const isDark = false; // Force white theme as per user request

  // Prevent scrolling when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      setIsSuccess(false);
      setSubmittedData(null);
      setIndustry("");
      setInterest("");
      setPhone("");
      setConsent(false);
    } else {
      document.body.style.overflow = "unset";
      setIsSuccess(false);
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // ── Validation helpers ──
  const validateEmail = (email) => {
    // Must have @ with domain containing at least one dot
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return re.test(email);
  };

  const validatePhone = (val) => {
    // Strip everything except digits and leading +
    const digits = val.replace(/[^0-9]/g, "");
    return digits.length >= 7 && digits.length <= 15;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = e.target;
    const formData = new FormData(form);
    const data = Object.fromEntries(formData.entries());

    // ── Validate before submitting ──
    const newErrors = {};

    if (!(data.name || "").trim()) {
      newErrors.name = "Name is required";
    }

    if (!validateEmail(data.email || "")) {
      newErrors.email = "Please enter a valid email (e.g. name@company.com)";
    }

    // Phone is optional; only validate format if a number was typed
    if (phone.trim() && !validatePhone(phone)) {
      newErrors.phone = "Please enter a valid phone number (7–15 digits)";
    }

    if (!(data.company || "").trim()) {
      newErrors.company = "Company name is required";
    }

    if (!isDownload && !interest) {
      newErrors.interest = "Please select what you're interested in";
    }

    if (!isDownload && !industry) {
      newErrors.industry = "Please select your industry";
    }

    if (!isDownload && !(data.message || "").trim()) {
      newErrors.message = "Message is required";
    }

    if (!consent) {
      newErrors.consent = "You must agree to the Privacy Policy to proceed";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    data.interest = interest || "General";
    data.industry = industry || "Not specified";
    data.phone = phone.trim() || "Not provided";

    // IMPORTANT: Start PDF prefetch IMMEDIATELY (while Chrome has user-gesture context)
    // This runs in parallel with form submission so the blob is ready when we need it
    let downloadReady = null;
    if (isDownload) {
      downloadReady = prefetchDownload(downloadKey);
    }

    const finalMessage = isDownload
      ? `DOWNLOAD REQUEST — ${downloadKey}`
      : intent === "download"
        ? `DOWNLOAD REQUEST\n${data.message}`
        : `[Interest: ${data.interest}]\n${data.message}`;

    // Detect user location with multi-tiered fallback
    let detectedLocation = "";
    try {
      // Primary: ipwho.is (very accurate city-level lookup, free & CORS enabled)
      const res = await fetch("https://ipwho.is/");
      const geo = await res.json();
      if (geo && geo.success) {
        const parts = [geo.city, geo.region, geo.country].filter(Boolean);
        if (parts.length > 0) detectedLocation = parts.join(", ");
      }
    } catch {
      /* fallback to next service */
    }

    if (!detectedLocation) {
      try {
        // Fallback 1: geojs.io (join whatever fields are present)
        const locRes = await fetch("https://get.geojs.io/v1/ip/geo.json");
        const locData = await locRes.json();
        if (locData) {
          const parts = [locData.city, locData.region, locData.country].filter(Boolean);
          if (parts.length > 0) detectedLocation = parts.join(", ");
        }
      } catch {
        /* fallback to timezone */
      }
    }

    // Fallback 2: Local browser timezone (works even when adblockers block IP services)
    if (!detectedLocation) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) detectedLocation = `Timezone: ${tz}`;
      } catch {
        /* ignore */
      }
    }

    data.location = detectedLocation || "Location unavailable";

    // Background email dispatch to Node.js Express server
    try {
      const BACKEND_URL = "https://osticket.anugraha.co/send-email";
      fetch(BACKEND_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          company: data.company || "Not provided",
          interest: data.interest,
          industry: data.industry,
          message: finalMessage,
          location: data.location,
        }),
      }).catch((err) => {
        console.warn("Backend email service notification:", err);
      });
    } catch (emailError) {
      /* ignore setup error */
    }

    try {
      const collectionName = isDownload ? "downloads" : "contacts";
      await addDoc(collection(db, collectionName), {
        name: data.name,
        email: data.email,
        phone: data.phone,
        company: data.company || "Not provided",
        interest: data.interest,
        industry: data.industry,
        message: finalMessage,
        intent: intent,
        downloadKey: downloadKey || null,
        location: data.location || "Location unavailable",
        createdAt: serverTimestamp(),
      });
    } catch (firebaseError) {
      console.error("Firebase save failed:", firebaseError);
    }

    setSubmittedData({
      name: data.name,
      email: data.email,
      company: data.company,
      interest: data.interest,
      industry: data.industry,
    });
    setIsSuccess(true);

    // Trigger the download from the prefetched blob
    if (downloadReady) {
      try {
        const prefetched = await downloadReady;
        triggerPrefetchedDownload(prefetched);
      } catch (dlErr) {
        console.warn("Download trigger failed:", dlErr);
      }
    }

    setIsSubmitting(false);
  };

  if (typeof document === "undefined") return null;

  // Staggered field animation config
  const fieldVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        delay: 0.15 + i * 0.06,
        ease: [0.22, 1, 0.36, 1],
      },
    }),
  };

  const inputClasses = isDark
    ? "bg-slate-900/50 border border-slate-800 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 transition-all font-medium text-sm"
    : "bg-slate-50 border border-slate-200 rounded-xl px-3.5 sm:px-4 py-3 sm:py-3.5 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all font-medium text-sm sm:text-base hover:border-slate-300";

  const labelClasses = isDark
    ? "text-[10px] text-slate-500 font-bold tracking-widest uppercase mb-1.5"
    : "text-[11px] text-slate-500 font-semibold tracking-wider uppercase";

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-100 bg-black/60 backdrop-blur-md h-[100svh]"
          />

          {/* Modal Content */}
          <div className="fixed inset-0 z-101 flex items-center justify-center p-3 sm:p-6 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 30 }}
              transition={{ type: "spring", duration: 0.6, bounce: 0.1 }}
              className={`w-full max-w-3xl rounded-2xl sm:rounded-3xl ${
                isDark ? "bg-[#0a101d]" : "bg-white"
              } shadow-[0_30px_100px_-20px_rgba(0,0,0,0.3),0_0_0_1px_rgba(0,0,0,0.05)] pointer-events-auto relative flex flex-col max-h-[92svh] sm:max-h-[88vh] overflow-hidden`}
            >
              <div className="relative flex-1 overflow-y-auto custom-scrollbar">
                {!isDark && (
                  <>
                    <div className="absolute -top-20 -right-20 w-60 h-60 bg-blue-100/50 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-indigo-100/40 rounded-full blur-3xl pointer-events-none" />
                  </>
                )}

                {isSuccess ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="p-8 md:p-12 text-center flex flex-col items-center justify-center min-h-[380px] relative z-10"
                  >
                    {/* Close Button */}
                    <motion.button
                      onClick={onClose}
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      transition={{ duration: 0.2 }}
                      className={`absolute top-4 right-4 ${
                        isDark
                          ? "text-slate-500 hover:text-white"
                          : "text-slate-400 hover:text-slate-700"
                      } transition-colors p-2 rounded-full hover:bg-slate-100/10`}
                      aria-label="Close modal"
                    >
                      <X size={20} />
                    </motion.button>

                    {/* Success Icon Badge */}
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{
                        type: "spring",
                        stiffness: 260,
                        damping: 20,
                        delay: 0.1,
                      }}
                      className="w-20 h-20 rounded-3xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-xl shadow-emerald-500/20 mb-6"
                    >
                      <CheckCircle2 className="w-10 h-10 text-white stroke-[2.5]" />
                    </motion.div>

                    {/* Title */}
                    <motion.h3
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.15 }}
                      className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight mb-2"
                    >
                      {isDownload
                        ? "Download Started!"
                        : isWalkthrough
                          ? "Walkthrough Requested!"
                          : `Thank You, ${submittedData?.name || "there"}!`}
                    </motion.h3>

                    {/* Subtitle */}
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.2 }}
                      className="text-slate-500 text-sm md:text-base max-w-md mb-6 leading-relaxed"
                    >
                      {isDownload
                        ? "Your technical brief download has initiated. We've also received your details."
                        : isWalkthrough
                          ? "Your scoped walkthrough request has been received. Our domain specialist will contact you within 24 hours."
                          : "We have received your enquiry. Our team will review your requirements and get back to you shortly."}
                    </motion.p>

                    {/* Done Button */}
                    <motion.button
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.3 }}
                      onClick={handleBackToHome}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full max-w-xs py-3.5 rounded-xl font-bold text-sm bg-slate-900 text-white hover:bg-slate-800 transition-all shadow-md"
                    >
                      Back to Home
                    </motion.button>
                  </motion.div>
                ) : (
                  <div className="p-4 sm:p-8 md:p-10 text-center flex flex-col items-center relative z-10">
                  {/* Close Button */}
                  <motion.button
                    onClick={onClose}
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                    className={`absolute top-3 right-3 sm:top-4 sm:right-4 ${
                      isDark
                        ? "text-slate-500 hover:text-white"
                        : "text-slate-400 hover:text-slate-700"
                    } transition-colors p-2 rounded-full hover:bg-slate-100/10 z-20`}
                    aria-label="Close modal"
                  >
                    <X size={20} />
                  </motion.button>

                  {/* ── DOWNLOAD-GATED HEADER ── */}
                  {isDownload && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="px-4 py-1.5 rounded-full border border-blue-500/20 bg-blue-50 text-blue-600 text-[11px] font-bold tracking-wider mb-4 sm:mb-8 flex items-center gap-2"
                    >
                      <Download size={12} /> Technical Brief · Instant Download
                    </motion.div>
                  )}

                  {/* ── WALKTHROUGH HEADER ── */}
                  {isWalkthrough && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="px-4 py-1.5 rounded-full border border-blue-500/20 bg-blue-50 text-blue-600 text-[11px] font-bold tracking-wider mb-4 sm:mb-8"
                    >
                      30 minutes · Domain specialist · Your environment
                    </motion.div>
                  )}

                  {/* ── CONTACT HEADER ICON ── */}
                  {!isWalkthrough && !isDownload && (
                    <motion.div
                      className="mb-3 sm:mb-5"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{
                        type: "spring",
                        duration: 0.7,
                        delay: 0.1,
                        bounce: 0.4,
                      }}
                    >
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-200">
                        <HeartHandshake className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                      </div>
                    </motion.div>
                  )}

                  {/* Text */}
                  <motion.h2
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.15 }}
                    className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight mb-1 sm:mb-2 text-slate-900"
                  >
                    {isDownload
                      ? "Download Technical Brief"
                      : isWalkthrough
                        ? "Request a Scoped Walkthrough"
                        : "Get in Touch"}
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 }}
                    className="text-slate-500 text-xs sm:text-sm mb-4 sm:mb-8 max-w-md"
                  >
                    {isDownload
                      ? "Fill in your details to receive the document."
                      : isWalkthrough
                        ? "Not a generic demo. Your sector, your standards, your questions."
                        : "Choose your preferred method to connect with our team."}
                  </motion.p>

                  {/* Contact Form */}
                  <div className="w-full text-left">
                    <form
                      className="grid grid-cols-1 md:grid-cols-2 gap-3.5 sm:gap-5"
                      onSubmit={handleSubmit}
                      noValidate
                    >
                      {/* ── NAME ── */}
                      <motion.div
                        className="flex flex-col gap-1.5"
                        custom={1}
                        variants={fieldVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <label className={labelClasses}>
                          Your Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          className={`${inputClasses} ${errors.name ? "!border-red-500 !ring-2 !ring-red-100 !bg-red-50/30" : ""}`}
                          placeholder="Enter your name"
                          onChange={() =>
                            errors.name &&
                            setErrors((prev) => ({ ...prev, name: "" }))
                          }
                        />
                        {errors.name && (
                          <motion.div
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-2 mt-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200"
                          >
                            <span className="text-red-500 text-xs">⚠</span>
                            <span className="text-red-600 text-[11px] font-bold">
                              {errors.name}
                            </span>
                          </motion.div>
                        )}
                      </motion.div>

                      {/* ── EMAIL ── */}
                      <motion.div
                        className="flex flex-col gap-1.5"
                        custom={2}
                        variants={fieldVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <label className={labelClasses}>
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          className={`${inputClasses} ${errors.email ? "!border-red-500 !ring-2 !ring-red-100 !bg-red-50/30" : ""}`}
                          placeholder="name@company.com"
                          required
                          onChange={() =>
                            errors.email &&
                            setErrors((prev) => ({ ...prev, email: "" }))
                          }
                        />
                        {errors.email && (
                          <motion.div
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-start gap-2 mt-1.5 px-3 py-2 rounded-lg bg-red-50 border border-red-200"
                          >
                            <span className="text-red-500 text-sm mt-0.5">
                              ⚠
                            </span>
                            <div className="flex flex-col">
                              <span className="text-red-600 text-[11px] font-bold">
                                {errors.email}
                              </span>
                              <span className="text-red-400 text-[10px] mt-0.5">
                                Format: name@company.com
                              </span>
                            </div>
                          </motion.div>
                        )}
                      </motion.div>

                      {/* ── PHONE ── */}
                      <motion.div
                        className="flex flex-col gap-1.5"
                        custom={3}
                        variants={fieldVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <label className={labelClasses}>
                          Phone Number{" "}
                          <span className="text-slate-400 font-normal lowercase tracking-normal text-[11px]">
                            (optional)
                          </span>
                        </label>
                        <CustomPhoneInput
                          value={phone}
                          onChange={(val) => {
                            setPhone(val || "");
                            if (errors.phone) {
                              setErrors((prev) => ({ ...prev, phone: "" }));
                            }
                          }}
                          error={!!errors.phone}
                        />
                        {errors.phone && (
                          <motion.div
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-start gap-2 mt-1.5 px-3 py-2 rounded-lg bg-red-50 border border-red-200"
                          >
                            <span className="text-red-500 text-sm mt-0.5">
                              ⚠
                            </span>
                            <div className="flex flex-col">
                              <span className="text-red-600 text-[11px] font-bold">
                                {errors.phone}
                              </span>
                              <span className="text-red-400 text-[10px] mt-0.5">
                                Format: +91 98765 43210
                              </span>
                            </div>
                          </motion.div>
                        )}
                      </motion.div>

                      {/* ── COMPANY ── */}
                      <motion.div
                        className="flex flex-col gap-1.5"
                        custom={4}
                        variants={fieldVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <label className={labelClasses}>
                          Company / Organization <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="company"
                          className={`${inputClasses} ${errors.company ? "!border-red-500 !ring-2 !ring-red-100 !bg-red-50/30" : ""}`}
                          placeholder="Enter your company name"
                          onChange={() =>
                            errors.company &&
                            setErrors((prev) => ({ ...prev, company: "" }))
                          }
                        />
                        {errors.company && (
                          <motion.div
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-2 mt-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200"
                          >
                            <span className="text-red-500 text-xs">⚠</span>
                            <span className="text-red-600 text-[11px] font-bold">
                              {errors.company}
                            </span>
                          </motion.div>
                        )}
                      </motion.div>

                                      {/* ── INTEREST & INDUSTRY (hidden for download-gated) ── */}
                      {!isDownload && (
                        <>
                          {/* ── ROUTING INTEREST ── */}
                          <motion.div
                            className="flex flex-col gap-1.5"
                            custom={4}
                            variants={fieldVariants}
                            initial="hidden"
                            animate="visible"
                          >
                            <label className={labelClasses}>
                              I'm interested in{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <CustomSelect
                              value={interest}
                              onChange={(val) => {
                                setInterest(val);
                                if (errors.interest) {
                                  setErrors((prev) => ({
                                    ...prev,
                                    interest: "",
                                  }));
                                }
                              }}
                              placeholder="Select enquiry topic"
                              options={INTEREST_OPTIONS}
                              error={!!errors.interest}
                              isDark={isDark}
                            />
                            {errors.interest && (
                              <motion.div
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex items-center gap-2 mt-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200"
                              >
                                <span className="text-red-500 text-xs">⚠</span>
                                <span className="text-red-600 text-[11px] font-bold">
                                  {errors.interest}
                                </span>
                              </motion.div>
                            )}
                          </motion.div>

                          {/* ── INDUSTRY ── */}
                          <motion.div
                            className="flex flex-col gap-1.5"
                            custom={5}
                            variants={fieldVariants}
                            initial="hidden"
                            animate="visible"
                          >
                            <label className={labelClasses}>
                              Industry <span className="text-red-500">*</span>
                            </label>
                            <CustomSelect
                              value={industry}
                              onChange={(val) => {
                                setIndustry(val);
                                if (errors.industry) {
                                  setErrors((prev) => ({
                                    ...prev,
                                    industry: "",
                                  }));
                                }
                              }}
                              placeholder="Select your industry"
                              options={INDUSTRY_OPTIONS}
                              error={!!errors.industry}
                              isDark={isDark}
                            />
                            {errors.industry && (
                              <motion.div
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex items-center gap-2 mt-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200"
                              >
                                <span className="text-red-500 text-xs">⚠</span>
                                <span className="text-red-600 text-[11px] font-bold">
                                  {errors.industry}
                                </span>
                              </motion.div>
                            )}
                          </motion.div>
                        </>
                      )}

                      {/* ── MESSAGE (hidden for download-gated) ── */}
                      {!isDownload && (
                        <motion.div
                          className="flex flex-col gap-1.5 md:col-span-2"
                          custom={5}
                          variants={fieldVariants}
                          initial="hidden"
                          animate="visible"
                        >
                          <label className={labelClasses}>
                            {isWalkthrough
                              ? "Specific Requirements "
                              : "Message "}
                            <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            rows={isWalkthrough ? "3" : "4"}
                            name="message"
                            className={`${inputClasses} resize-none ${errors.message ? "!border-red-500 !ring-2 !ring-red-100 !bg-red-50/30" : ""}`}
                            placeholder={
                              isWalkthrough
                                ? "Tell us about your operational environment..."
                                : "Tell us how we can help you..."
                            }
                            onChange={() =>
                              errors.message &&
                              setErrors((prev) => ({ ...prev, message: "" }))
                            }
                          ></textarea>
                          {errors.message && (
                            <motion.div
                              initial={{ opacity: 0, y: -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="flex items-center gap-2 mt-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200"
                            >
                              <span className="text-red-500 text-xs">⚠</span>
                              <span className="text-red-600 text-[11px] font-bold">
                                {errors.message}
                              </span>
                            </motion.div>
                          )}
                        </motion.div>
                      )}

                      {/* ── PRIVACY CONSENT CHECKBOX ── */}
                      <motion.div
                        className="md:col-span-2 flex flex-col gap-1 mt-1"
                        custom={6}
                        variants={fieldVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <label className="flex items-start gap-2.5 cursor-pointer group">
                          <input
                            type="checkbox"
                            name="consent"
                            checked={consent}
                            onChange={(e) => {
                              setConsent(e.target.checked);
                              if (errors.consent) {
                                setErrors((prev) => ({ ...prev, consent: "" }));
                              }
                            }}
                            className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer shrink-0 accent-blue-600"
                          />
                          <span className="text-xs text-slate-600 leading-normal select-none">
                            I agree to the collection and processing of my data in accordance with the{" "}
                            <Link
                              to="/privacy-policy"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 font-semibold hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Privacy Policy
                            </Link>
                            . <span className="text-red-500">*</span>
                          </span>
                        </label>
                        {errors.consent && (
                          <motion.div
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-2 mt-1 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200"
                          >
                            <span className="text-red-500 text-xs">⚠</span>
                            <span className="text-red-600 text-[11px] font-bold">
                              {errors.consent}
                            </span>
                          </motion.div>
                        )}
                      </motion.div>

                      {/* ── SUBMIT BUTTON ── */}
                      <motion.div
                        className="md:col-span-2 flex flex-col items-center justify-center mt-3"
                        custom={6}
                        variants={fieldVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <motion.button
                          type="submit"
                          disabled={isSubmitting || isSuccess}
                          whileHover={{
                            scale: 1.02,
                            boxShadow: "0 8px 30px -8px rgba(59,130,246,0.5)",
                          }}
                          whileTap={{ scale: 0.98 }}
                          className={`w-full py-3.5 rounded-xl font-bold text-sm tracking-wide transition-all duration-300 flex items-center justify-center gap-2 ${
                            isSuccess
                              ? "bg-emerald-500 text-white shadow-lg shadow-emerald-200"
                              : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-200 hover:from-blue-700 hover:to-indigo-700"
                          } disabled:opacity-70 disabled:cursor-not-allowed`}
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="animate-spin" size={16} />
                              Sending...
                            </>
                          ) : isSuccess ? (
                            <>
                              <CheckCircle2 size={16} />
                              {isDownload
                                ? "Download Starting..."
                                : "Request Sent!"}
                            </>
                          ) : (
                            <>
                              {isDownload
                                ? "Submit & Download"
                                : isWalkthrough
                                  ? "Request Walkthrough →"
                                  : "Submit Request"}
                            </>
                          )}
                        </motion.button>

                        {/* Disclaimer Text */}
                        <p className="text-[12px] text-slate-500 font-medium text-center mt-4">
                          No commitment. NDA available on request.
                        </p>
                      </motion.div>
                    </form>
                  </div>
                </div>
              )}
            </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

const INTEREST_OPTIONS = [
  { value: "Demo", label: "Product Demo / Walkthrough", icon: Video },
  { value: "Partnership", label: "Partnership Opportunities", icon: Handshake },
  { value: "Press", label: "Press & Media", icon: Newspaper },
  { value: "Careers", label: "Careers & Talent", icon: Users },
  { value: "General", label: "General Inquiry", icon: MessageSquare },
];

const INDUSTRY_OPTIONS = [
  { value: "Aviation", label: "Aviation", icon: Plane },
  { value: "Defence", label: "Defence", icon: Shield },
  { value: "Energy", label: "Energy", icon: Zap },
];

const CustomSelect = ({
  value,
  onChange,
  placeholder,
  options,
  error,
  isDark = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-200 text-left ${
          isDark
            ? "bg-slate-900/50 border border-slate-800 text-white focus:outline-none focus:border-cyan-500/50"
            : "bg-slate-50 border border-slate-200 text-slate-800 hover:border-slate-300 hover:bg-slate-100/50"
        } ${
          error
            ? "!border-red-500 !ring-2 !ring-red-100 !bg-red-50/30"
            : isOpen
              ? "!border-blue-400 !ring-2 !ring-blue-100 bg-white shadow-sm"
              : ""
        }`}
      >
        <span
          className={
            selectedOption
              ? "text-slate-900 font-semibold flex items-center gap-2.5 truncate pr-2"
              : "text-slate-400 font-normal truncate pr-2"
          }
        >
          {selectedOption ? (
            <>
              {selectedOption.icon && (
                <selectedOption.icon size={16} className="text-blue-600 shrink-0" />
              )}
              <span className="truncate">{selectedOption.label}</span>
            </>
          ) : (
            placeholder
          )}
        </span>
        <ChevronDown
          size={16}
          className={`text-slate-400 shrink-0 transition-transform duration-300 ${
            isOpen ? "rotate-180 text-blue-500" : ""
          }`}
        />
      </button>

      {/* Floating Animated Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 4, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="absolute left-0 right-0 z-[120] mt-1 max-h-60 overflow-y-auto rounded-2xl border border-slate-100 bg-white/95 backdrop-blur-xl p-1.5 shadow-[0_20px_50px_-10px_rgba(0,0,0,0.18)] custom-scrollbar"
          >
            {options.map((opt) => {
              const isSelected = opt.value === value;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 text-left ${
                    isSelected
                      ? "bg-blue-50 text-blue-600 font-semibold"
                      : "text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0 pr-2">
                    {Icon && (
                      <Icon
                        size={16}
                        className={`shrink-0 ${isSelected ? "text-blue-600" : "text-slate-400"}`}
                      />
                    )}
                    <span className="truncate">{opt.label}</span>
                  </div>
                  {isSelected && (
                    <Check size={16} className="text-blue-600 stroke-[2.5] shrink-0" />
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const CustomPhoneInput = ({ value, onChange, error }) => {
  const [selectedCountry, setSelectedCountry] = useState("IN");
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = React.useRef(null);

  const countries = getCountries();
  const callingCode = `+${getCountryCallingCode(selectedCountry)}`;

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredCountries = countries.filter((code) => {
    const countryName = en[code] || "";
    const dialCode = getCountryCallingCode(code);
    const query = searchQuery.toLowerCase().trim();
    return (
      countryName.toLowerCase().includes(query) ||
      dialCode.includes(query) ||
      code.toLowerCase().includes(query)
    );
  });

  const SelectedFlag = flags[selectedCountry];

  const handleNumberChange = (e) => {
    const rawDigits = e.target.value.replace(/[^0-9 ]/g, "");
    onChange(`${callingCode} ${rawDigits}`);
  };

  const getDisplayNumber = () => {
    if (!value) return "";
    if (value.startsWith(callingCode)) {
      return value.slice(callingCode.length).trim();
    }
    return value;
  };

  return (
    <div className="flex gap-2 w-full">
      {/* Country Select Button */}
      <div className="relative shrink-0" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className={`h-[48px] flex items-center gap-2 px-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 text-sm font-medium hover:border-slate-300 transition-all ${
            isOpen ? "!border-blue-400 !ring-2 !ring-blue-100 bg-white shadow-xs" : ""
          }`}
        >
          {SelectedFlag && (
            <div className="w-5 h-3.5 rounded-xs overflow-hidden shadow-xs shrink-0">
              <SelectedFlag title={en[selectedCountry]} />
            </div>
          )}
          <span className="font-semibold text-slate-900 text-xs sm:text-sm">
            {callingCode}
          </span>
          <ChevronDown
            size={14}
            className={`text-slate-400 transition-transform duration-300 ${
              isOpen ? "rotate-180 text-blue-500" : ""
            }`}
          />
        </button>

        {/* Dropdown List Below with Search Bar */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.98 }}
              animate={{ opacity: 1, y: 4, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.98 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="absolute left-0 z-[150] mt-1 w-[calc(100vw-3.5rem)] max-w-xs sm:w-72 max-h-72 overflow-hidden rounded-2xl border border-slate-100 bg-white/95 backdrop-blur-xl shadow-[0_20px_50px_-10px_rgba(0,0,0,0.18)] flex flex-col"
            >
              {/* Sticky Search Input Bar */}
              <div className="p-2 border-b border-slate-100 bg-slate-50/60 sticky top-0 z-10">
                <div className="relative flex items-center">
                  <Search size={14} className="absolute left-3 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search country or code..."
                    className="w-full bg-white border border-slate-200 rounded-xl pl-8 pr-3 py-2 text-xs text-slate-800 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 placeholder:text-slate-400"
                    autoFocus
                  />
                </div>
              </div>

              {/* Filtered Countries */}
              <div className="overflow-y-auto max-h-56 p-1.5 custom-scrollbar">
                {filteredCountries.length === 0 ? (
                  <div className="py-4 text-center text-xs text-slate-400 font-medium">
                    No country found
                  </div>
                ) : (
                  filteredCountries.map((code) => {
                    const Flag = flags[code];
                    const name = en[code];
                    const dial = `+${getCountryCallingCode(code)}`;
                    const isSelected = code === selectedCountry;

                    return (
                      <button
                        key={code}
                        type="button"
                        onClick={() => {
                          setSelectedCountry(code);
                          setIsOpen(false);
                          setSearchQuery("");
                          const currentDigits = getDisplayNumber();
                          onChange(`+${getCountryCallingCode(code)} ${currentDigits}`);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium transition-all text-left ${
                          isSelected
                            ? "bg-blue-50 text-blue-600 font-semibold"
                            : "text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                        }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0 pr-2">
                          {Flag && (
                            <div className="w-5 h-3.5 rounded-xs overflow-hidden shadow-xs shrink-0">
                              <Flag title={name} />
                            </div>
                          )}
                          <span className="truncate">{name}</span>
                          <span className="text-slate-400 text-[11px] font-normal shrink-0">
                            ({dial})
                          </span>
                        </div>
                        {isSelected && (
                          <Check size={14} className="text-blue-600 stroke-[2.5] shrink-0" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Phone Number Input */}
      <input
        type="tel"
        value={getDisplayNumber()}
        onChange={handleNumberChange}
        placeholder="98765 43210"
        className={`flex-1 h-[48px] bg-slate-50 border border-slate-200 rounded-xl px-4 text-slate-800 text-sm font-medium transition-all hover:border-slate-300 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 placeholder:text-slate-400 ${
          error ? "!border-red-500 !ring-2 !ring-red-100 !bg-red-50/30" : ""
        }`}
      />
    </div>
  );
};

export default ContactModal;
